import React, {useContext, useState} from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    List,
    ListItem,
    ListItemText, MobileStepper, Paper, Step, StepLabel, Stepper,
    Typography
} from "@mui/material";
import Grid from '@mui/material/Grid';
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import {PAContext} from "../../Services/PAContext";

function CreateNewPlanning({ isOpen, onClose }){

    const { dataArray } = useContext(PAContext);
    const { Account, Attendees } = dataArray;

    const [activeStep, setActiveStep] = useState(0);
    const eventCount = Account.events?.length || 0;

    const steps = [
        {
            label: '🚀 How It Works',
            content: (
                <>
                    <Typography>
                        You’re about to start a <strong>Planning Session</strong> — your sandbox for dreaming, building,
                        and organizing your next great event. Explore features, get familiar with the dashboard,
                        and start collecting everything you need to bring your vision to life.
                    </Typography>
                    <Typography>
                        When you’re ready, you can save your Planning as:
                        <ul style={{ marginLeft: '1em', marginTop: '0.5em' }}>
                            <li><strong>An Event</strong> — ready to go live</li>
                            <li><strong>A Template</strong> — reusable for future awesomeness</li>
                        </ul>
                    </Typography>

                    {/* Save Reminder */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        ⚠️ Don’t Forget to Save!
                    </Typography>
                    <Typography>
                        During Planning, make sure to save any progress before closing the tab or
                        browser - <strong>we do not auto-save</strong> — we’d hate for all that magic to disappear!
                    </Typography>

                    {/* Host Tiers */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        🎯 Host Tiers: Your Journey, Rewarded
                    </Typography>
                    <Typography>
                        Every event you host levels you up — with perks, discounts, and recognition.
                        Here's how you level up:
                    </Typography>

                    <List>
                        {/* Tier Items */}
                        {[
                            {
                                icon: <i className="icon-happy" style={{color: 'green', fontSize: '16px', marginRight: '8px'}}/>,
                                title: "Newbie Host",
                                subtitle: "1–3 Events → 5% off your next event"
                            },
                            {
                                icon: <i className="icon-fire" style={{color: 'orangered', fontSize: '16px', marginRight: '8px'}}/>,
                                title: "Rising Star Host",
                                subtitle: "4–10 Events → 15% off for you + 1 friend"
                            },
                            {
                                icon: <i className="icon-diamond2" style={{color: 'silver', fontSize: '16px', marginRight: '8px'}}/>,
                                title: "Pro Host",
                                subtitle: "11–20 Events → 20% off for you + 1 friend"
                            },
                            {
                                icon: <i className="icon-crown" style={{color: 'purple', fontSize: '16px', marginRight: '8px'}}/>,
                                title: "Master Host",
                                subtitle: "21–50 Events → 25% off + friend + optional newsletter spotlight"
                            },
                            {
                                icon: <i className="icon-star" style={{color: 'goldenrod', fontSize: '16px', marginRight: '8px'}}/>,
                                title: "Legendary Host",
                                subtitle: "50+ Events → Lifetime perks: free events, premium tools, and early access"
                            }
                        ].map((tier, idx) => (
                            <React.Fragment key={tier.title}>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center">
                                                {tier.icon}
                                                <strong>{tier.title}</strong>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                                                {tier.subtitle}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {idx !== 4 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </>
            ),
        },
        {
            label: '🌟 Rising Star!',
            content: (
                <>
                    <Typography>
                        Look at you — <strong>{Account.events?.length}</strong> events deep and just getting started! You're not new to this, you're true to this 💪
                        We're thrilled to have you shaping amazing experiences all year round.
                    </Typography>

                    <Typography>
                        As a Rising Star Host, you now get a <strong>15% discount</strong> — and one <strong>for a friend</strong> on their next adventure.
                        A little thank-you for spreading the magic.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        🔧 Pro Tools, Just for You
                    </Typography>

                    <Typography component="ul" sx={{ pl: 3 }}>
                        <li>Save and reuse your best setups with <strong>Templates</strong></li>
                        <li>Co-create with friends using <strong>Co-hosts</strong></li>
                        <li>Share your plan link to collect ideas or RSVPs</li>
                        <li>Track budgets, vendors, and activity ideas in one place</li>
                    </Typography>

                    <Typography sx={{ mt: 2 }}>
                        You’re leveling up fast — next stop: Pro Host status!
                        More events, more perks, more power to plan your way.
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic', color: 'gray' }}>
                        Tip: Want to speed things up? Try turning your favorite plan into a reusable template.
                    </Typography>
                </>
            ),
        },
        {
            label: '🏆 Pro Host!',
            content: (
                <>
                    <Typography>
                        <strong>{Account.events?.length}</strong> events — that’s not just dedication, that’s craft. 🙌
                        You’ve built something special, and we’re here to help you keep raising the bar.
                    </Typography>

                    <Typography>
                        As a Pro Host, you now unlock a <strong>20% discount</strong> — for you and one lucky friend.
                        It's our way of saying thanks for making the magic happen again and again.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        🛠️ Power Features to Streamline Your Flow
                    </Typography>

                    <Typography component="ul" sx={{ pl: 3 }}>
                        <li><strong>Templates:</strong> Create repeatable setups in seconds</li>
                        <li><strong>Collaborators:</strong> Hand off tasks or co-host with ease</li>
                        <li><strong>Vendor & Budget Tools:</strong> Keep logistics smooth</li>
                        <li><strong>Planning Snapshots:</strong> Track changes & versions for peace of mind</li>
                    </Typography>

                    <Typography sx={{ mt: 2 }}>
                        <strong>Next stop: Master Host 🌟</strong><br />
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Almost there — just {21 - Account.events?.length} more events to unlock Master Host 🎯
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={(Account.events?.length / 21) * 100}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    backgroundColor: '#f0f0f0',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: '#4caf50'
                                    }
                                }}
                            />
                        </Box>
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic', color: 'gray' }}>
                        Tip: Streamline your next event by duplicating a past plan and tweaking the details.
                    </Typography>

                    <Box sx={{ mt: 4, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>📈 Your Journey So Far</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle1">Events Hosted</Typography>
                                    <Typography variant="h5" color="primary"><strong>{Account.events?.length}</strong></Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle1">Guests Reached</Typography>
                                    <Typography variant="h5" color="primary"><strong>{Attendees.length}</strong></Typography>
                                </Paper>
                            </Grid>
                            {/* You can add more: most used location, most repeated event type, etc. */}
                        </Grid>
                    </Box>
                </>
            ),
        },
        {
            label: `👑 Master Host`,
            content: (
                <>
                    <Typography>
                        <strong>{Account.events?.length}</strong> events. That’s not just commitment — it’s legacy.
                        You’ve hosted more moments, memories, and magic than most ever will.
                        We see you, and we’re here to champion you.
                    </Typography>

                    <Typography>
                        As a Master Host, you now unlock a <strong>25% discount</strong> — for you and a friend.
                        Plus, you’re eligible for our monthly <strong>Host Spotlight</strong>:
                        share your tips, inspire the community, and get featured in our newsletter!
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        🌐 Share Your Wisdom
                    </Typography>

                    <Typography component="ul" sx={{pl: 3}}>
                        <li>Turn your best setups into public templates</li>
                        <li>Create a reusable blueprint: Turn this trip into a public template</li>
                        <li>Streamline future planning: Save this setup as a public template</li>
                        <li>Turn your favorite trip plans into public templates for easy reuse and sharing</li>
                        <li>Streamline future planning and inspire others: Save this setup as a public template for easy use and personal customization</li>
                        <li>Replicate success and empower others: Make this trip template public, allowing everyone to use and adapt it to their unique preferences</li>
                        <li>Save time and help others get started: Share this setup as a public template they can easily use and adjust to their specific needs</li>
                        <li>Create a reusable blueprint and a starting point for others: Turn this trip into a public template that's fully customizable</li>
                        <li>Contribute your best trip structures as public templates, offering a flexible starting point for other travelers</li>
                    </Typography>

                    <Box sx={{mt: 4}}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Just {51 - Account.events?.length} events away from Legendary status 🌟
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={(Account.events?.length / 51) * 100}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: '#f0f0f0',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#673ab7'
                                }
                            }}
                        />
                    </Box>

                    <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic', color: 'gray' }}>
                        Tip: Reach out to the team if you’re open to sharing your story or hosting wisdom — we’d love to spotlight you!
                    </Typography>
                </>
            ),
        },
        {
            label: `🌟 Legendary Host`,
            content: (
                <>
                    <Typography>
                        <strong>{Account.events?.length}</strong> events. That’s not just impressive — it’s legendary.
                        You’ve brought people together, sparked unforgettable moments, and helped shape the soul of this platform.
                    </Typography>

                    <Typography>
                        You now enjoy <strong>lifetime benefits</strong>:
                    </Typography>

                    <Typography component="ul" sx={{ pl: 3 }}>
                        <li><strong>!1 events</strong> — forever</li>
                        <li><strong>Premium planning tools</strong> — automatically unlocked</li>
                        <li><strong>Early access</strong> to beta features and updates</li>
                        <li><strong>VIP priority support</strong> — when you need it, we’re there</li>
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        💬 Shape the Future
                    </Typography>

                    <Typography>
                        Want to help guide what we build next? Join our invite-only <strong>Circle</strong>
                        to test new tools, give feedback, and co-create what’s next with us.
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic', color: 'gray' }}>
                        You’re more than a host — you’re part of our story.
                        Thank you for building something extraordinary.
                    </Typography>
                </>
            ),
        },
    ];

    const handleNext = () => {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    return (
        <Dialog open={isOpen} onClose={onClose}>
            {/* Dialog Header */}
            <DialogTitle
                sx={{
                    color: '#151269',
                    textAlign: 'center',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    px: 3,
                    py: 2,
                    fontWeight: 'bold',
                    fontSize: '24px'
                }}
            >
                Plan Your Next Adventure
            </DialogTitle>

            <DialogTitle
                sx={{
                    fontSize: '16px',
                    textAlign: 'center',
                    color: '#151269',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    px: 3,
                    pb: 2,
                    fontWeight: 400,
                }}
            >
                Welcome to the fun part — a fresh blank canvas full of possibilities.
                Maybe some sunshine, definitely some memories, and absolutely an experience worth sharing. 🎈
                Let’s make something amazing.
            </DialogTitle>

            {/* Close Button */}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#151269',
                }}
            >
                <i className="icon-cross" style={{ fontSize: '18px', padding: '5px' }} />
            </IconButton>


            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel
                                onClick={() => setActiveStep(index)}
                                sx={{ cursor: 'pointer' }}
                            >
                                {step.label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ mt: 3 }}>
                    {steps[activeStep].content}
                </Box>

                <MobileStepper
                    variant="progress"
                    steps={steps.length}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === steps.length - 1}>
                            Next
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            Back
                        </Button>
                    }
                    sx={{ mt: 3, background: 'transparent', justifyContent: 'flex-end' }}
                />
            </DialogContent>
        </Dialog>

    )
}

export default CreateNewPlanning;